import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import DateInput from '../../ui/date-input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Loader2 } from 'lucide-react';
import api from '../../../utils/api';
import { prepareFileForUpload } from '../../../utils/imageUploadCompress';
import { useToast } from '../../../hooks/use-toast';

const LEAVE_TYPES = [
  { value: 'sick', label: 'Sick Leave' },
  { value: 'personal', label: 'Personal Leave' },
  { value: 'emergency', label: 'Emergency Leave' },
  { value: 'other', label: 'Other' },
];

function diffWorkingDays(from, to) {
  if (!from || !to || to < from) return 0;
  const start = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  let n = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) n += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return n;
}

export default function LeaveApplicationDialog({
  open,
  onOpenChange,
  student,
  parentMode,
  studentId,
  onSubmitted,
}) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [balances, setBalances] = useState([]);
  const [academicYearId, setAcademicYearId] = useState(null);
  const [form, setForm] = useState({
    leave_from: '',
    leave_to: '',
    leave_type: 'personal',
    reason: '',
  });
  const [file, setFile] = useState(null);

  const sid = parentMode ? studentId : student?.id;

  useEffect(() => {
    if (!open || !sid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const balanceUrl = parentMode
          ? '/portal/parent/leave-balance'
          : '/portal/student/leave-balance';
        const params = parentMode ? { student_id: sid } : {};
        const res = await api.get(balanceUrl, { params });
        if (cancelled) return;
        const payload = res.data?.data;
        if (Array.isArray(payload)) {
          setBalances(payload);
          setAcademicYearId(null);
        } else {
          setBalances(payload?.balances ?? []);
          setAcademicYearId(payload?.academic_year_id ?? null);
        }
      } catch {
        if (!cancelled) {
          setBalances([]);
          setAcademicYearId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, sid]);

  const daysCount = useMemo(
    () => diffWorkingDays(form.leave_from, form.leave_to),
    [form.leave_from, form.leave_to],
  );

  const balanceHint = balances.find((b) => b.leave_type === form.leave_type);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sid) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('leave_from', form.leave_from);
      fd.append('leave_to', form.leave_to);
      fd.append('leave_type', form.leave_type);
      fd.append('reason', form.reason);
      if (parentMode) fd.append('student_id', String(sid));
      if (academicYearId) fd.append('academic_year_id', String(academicYearId));
      if (file) {
        fd.append('attachment', await prepareFileForUpload(file));
      }

      const url = parentMode
        ? '/portal/parent/leave-applications'
        : '/portal/student/leave-applications';
      await api.post(url, fd);
      toast({ title: 'Leave submitted', description: 'Your request is pending approval.' });
      onOpenChange(false);
      setForm({ leave_from: '', leave_to: '', leave_type: 'personal', reason: '' });
      setFile(null);
      onSubmitted?.();
    } catch (err) {
      toast({
        title: 'Submit failed',
        description: err?.response?.data?.message || err?.message || 'Could not submit',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const name = student
    ? `${student.first_name || ''} ${student.last_name || ''}`.trim()
    : '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply for leave</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <Label className="text-muted-foreground text-xs">Student</Label>
              <p className="font-medium">{name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Admission no.</Label>
              <p className="font-medium">{student?.admission_no || '—'}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Class</Label>
              <p className="font-medium">{student?.class_name || '—'}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Section</Label>
              <p className="font-medium">{student?.section_name || '—'}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="leave_from">From date</Label>
              <DateInput
                id="leave_from"
                required
                value={form.leave_from}
                onChange={(v) => setForm((f) => ({ ...f, leave_from: v }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="leave_to">To date</Label>
              <DateInput
                id="leave_to"
                required
                value={form.leave_to}
                min={form.leave_from}
                onChange={(v) => setForm((f) => ({ ...f, leave_to: v }))}
              />
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Working days (approx.):
            {' '}
            <strong>{daysCount}</strong>
            {balanceHint ? (
              <>
                {' '}
                · Balance:
                {' '}
                <strong>{balanceHint.remaining_days}</strong>
                {' '}
                {form.leave_type}
                {' '}
                days left
              </>
            ) : null}
          </p>

          <div className="space-y-1">
            <Label>Leave type</Label>
            <Select
              value={form.leave_type}
              onValueChange={(v) => setForm((f) => ({ ...f, leave_type: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAVE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="reason">Reason</Label>
            <Textarea
              id="reason"
              required
              rows={3}
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="attachment">Attachment (optional)</Label>
            <Input
              id="attachment"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
